"use client"

import { useEffect, useState, useRef } from "react"
import axios from "axios"
import config from "../config"
import { Helmet } from 'react-helmet-async'
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { motion, AnimatePresence } from 'framer-motion'

// Import lucide-react icons
import { Bell, Check, CheckCheck, Eye, EyeOff, Search, RefreshCw, Filter, X, ChevronDown, Sparkles, Inbox, Clock, Tag, AlertCircle, CheckCircle, Info, Zap, Heart, Star, TrendingUp, Users, Calendar, DollarSign, MessageSquare, Shield } from 'lucide-react'

dayjs.extend(relativeTime)

const NotificationsPage = () => {
  const eventTitle = process.env.REACT_APP_EVENT_TITLE || "EventSawa"
  const eventDescription = process.env.REACT_APP_EVENT_DESCRIPTION || "We make events sawa"

  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [actionLoading, setActionLoading] = useState({})
  const [availableTypes, setAvailableTypes] = useState([])
  const [showFilters, setShowFilters] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [animatingNotifications, setAnimatingNotifications] = useState(new Set())

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/notifications/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      })
      setNotifications(res.data)

      // Extract unique notification types and titles dynamically
      const types = [...new Set(res.data.map((notif) => notif.type).filter(Boolean))]
      const titles = [...new Set(res.data.map((notif) => notif.title).filter(Boolean))]
      setAvailableTypes([...types, ...titles])
    } catch (err) {
      // console.error("Failed to fetch notifications", err)
    } finally {
      setLoading(false)
    }
  }

  const toggleReadStatus = async (notificationId, currentStatus) => {
    setActionLoading((prev) => ({ ...prev, [`read_${notificationId}`]: true }))
    setAnimatingNotifications(prev => new Set(prev).add(notificationId))
    
    try {
      const endpoint = `${config.API_BASE_URL}/notifications/${notificationId}/read/`
      
      if (!currentStatus) {
        try {
          const response = await axios.post(
            endpoint,
            {},
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
              },
            },
          )
          
          setNotifications((prev) =>
            prev.map((notif) => (notif.id === notificationId ? { ...notif, is_read: true } : notif)),
          )
        } catch (err) {
          alert('Failed to mark notification as read. Please try again.')
        }
      } else {
        setNotifications((prev) =>
          prev.map((notif) => (notif.id === notificationId ? { ...notif, is_read: false } : notif)),
        )
      }
    } catch (err) {
      alert('Failed to update notification status. Please try again.')
    } finally {
      setActionLoading((prev) => ({ ...prev, [`read_${notificationId}`]: false }))
      setTimeout(() => {
        setAnimatingNotifications(prev => {
          const newSet = new Set(prev)
          newSet.delete(notificationId)
          return newSet
        })
      }, 500)
    }
  }

  const toggleHideStatus = async (notificationId, currentStatus) => {
    setActionLoading((prev) => ({ ...prev, [`hide_${notificationId}`]: true }))
    try {
      await axios.patch(
        `${config.API_BASE_URL}/notifications/${notificationId}/`,
        { is_hidden: !currentStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      )

      setNotifications((prev) =>
        prev.map((notif) => (notif.id === notificationId ? { ...notif, is_hidden: !currentStatus } : notif)),
      )
    } catch (err) {
      // console.error("Failed to toggle hide status", err)
      // Still update locally
      setNotifications((prev) =>
        prev.map((notif) => (notif.id === notificationId ? { ...notif, is_hidden: !currentStatus } : notif)),
      )
    } finally {
      setActionLoading((prev) => ({ ...prev, [`hide_${notificationId}`]: false }))
    }
  }

  const markAllAsRead = async () => {
    setActionLoading((prev) => ({ ...prev, markAll: true }))
    try {
      const endpoint = `${config.API_BASE_URL}/notifications/mark-all-read/`
      
      try {
        const response = await axios.post(
          endpoint,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          },
        )
        
        setNotifications((prev) => prev.map((notif) => ({ ...notif, is_read: true })))
      } catch (err) {
        const unreadNotifications = notifications.filter(notif => !notif.is_read)
        
        if (unreadNotifications.length === 0) return
        
        let successCount = 0
        let failureCount = 0
        
        for (const notif of unreadNotifications) {
          try {
            const singleEndpoint = `${config.API_BASE_URL}/notifications/${notif.id}/read/`
            
            const singleResponse = await axios.post(
              singleEndpoint,
              {},
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                },
              },
            )
            successCount++
          } catch (singleErr) {
            failureCount++
          }
        }
        
        if (successCount > 0) {
          setNotifications((prev) => 
            prev.map((notif) => {
              const wasRead = unreadNotifications.some(n => n.id === notif.id)
              return wasRead ? { ...notif, is_read: true } : notif
            })
          )
        }
        
        if (failureCount > 0) {
          alert(`Successfully marked ${successCount} notifications as read, but ${failureCount} failed. Please try again.`)
        }
      }
    } catch (err) {
      alert('Failed to mark all notifications as read. Please try again.')
    } finally {
      setActionLoading((prev) => ({ ...prev, markAll: false }))
    }
  }

  const getNotificationIcon = (type) => {
    const iconMap = {
      bid: <TrendingUp className="w-5 h-5" />,
      event: <Calendar className="w-5 h-5" />,
      message: <MessageSquare className="w-5 h-5" />,
      payment: <DollarSign className="w-5 h-5" />,
      system: <Shield className="w-5 h-5" />,
      warning: <AlertCircle className="w-5 h-5" />,
      success: <CheckCircle className="w-5 h-5" />,
      info: <Info className="w-5 h-5" />,
      default: <Bell className="w-5 h-5" />,
    }
    return iconMap[type?.toLowerCase()] || iconMap.default
  }

  const getNotificationGradient = (type) => {
    const gradientMap = {
      bid: "from-blue-500 to-cyan-500",
      event: "from-purple-500 to-pink-500",
      message: "from-green-500 to-emerald-500",
      payment: "from-yellow-500 to-orange-500",
      system: "from-gray-500 to-slate-500",
      warning: "from-orange-500 to-red-500",
      success: "from-emerald-500 to-green-500",
      info: "from-blue-500 to-indigo-500",
      default: "from-gray-500 to-gray-600",
    }
    return gradientMap[type?.toLowerCase()] || gradientMap.default
  }

  const getNotificationColor = (type) => {
    const colorMap = {
      bid: "bg-blue-50 text-blue-700 border-blue-200",
      event: "bg-purple-50 text-purple-700 border-purple-200",
      message: "bg-green-50 text-green-700 border-green-200",
      payment: "bg-yellow-50 text-yellow-700 border-yellow-200",
      system: "bg-gray-50 text-gray-700 border-gray-200",
      warning: "bg-orange-50 text-orange-700 border-orange-200",
      success: "bg-emerald-50 text-emerald-700 border-emerald-200",
      info: "bg-blue-50 text-blue-700 border-blue-200",
      default: "bg-gray-50 text-gray-700 border-gray-200",
    }
    return colorMap[type?.toLowerCase()] || colorMap.default
  }

  const filteredNotifications = notifications
    .filter((notif) => !notif.is_hidden) // Don't show hidden notifications
    .filter((notif) => {
      if (filter === "unread") return !notif.is_read
      if (filter === "read") return notif.is_read
      if (filter !== "all") {
        return (
          notif.type?.toLowerCase() === filter.toLowerCase() ||
          notif.title?.toLowerCase() === filter.toLowerCase()
        )
      }
      return true
    })
    .filter(
      (notif) =>
        notif.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        notif.message?.toLowerCase().includes(searchQuery.toLowerCase()),
    )

  const unreadCount = notifications.filter((notif) => !notif.is_read && !notif.is_hidden).length

  useEffect(() => {
    fetchNotifications()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <Helmet>
          <title>{eventTitle}</title>
          <meta name="description" content={eventDescription} />
        </Helmet>
        <div className="max-w-6xl mx-auto p-6">
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="animate-pulse">
              <div className="h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-2xl w-1/3 mb-8"></div>
              <div className="space-y-6">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex space-x-4 p-6 border border-gray-100 rounded-2xl">
                    <div className="w-14 h-14 bg-gradient-to-r from-gray-200 to-gray-300 rounded-2xl"></div>
                    <div className="flex-1 space-y-3">
                      <div className="h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded-xl w-3/4"></div>
                      <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded-xl w-1/2"></div>
                      <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded-xl w-1/3"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Helmet>
        <title>{eventTitle}</title>
        <meta name="description" content={eventDescription} />
      </Helmet>

      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-8 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur-lg opacity-50"></div>
                <div className="relative bg-gradient-to-r from-blue-500 to-purple-500 p-3 rounded-2xl text-white">
                  <Bell className="w-8 h-8" />
                </div>
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent flex items-center gap-3">
                  Notifications
                  {unreadCount > 0 && (
                    <motion.span 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-sm px-3 py-1 rounded-full font-medium shadow-lg"
                    >
                      {unreadCount}
                    </motion.span>
                  )}
                </h1>
                <p className="text-gray-600 mt-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-yellow-500" />
                  Stay updated with your latest activities
                </p>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={markAllAsRead}
              disabled={actionLoading.markAll || unreadCount === 0}
              className={`px-8 py-4 rounded-2xl font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 shadow-xl hover:shadow-2xl border border-white/20 ${
                unreadCount > 0 
                  ? "bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              }`}
            >
              {actionLoading.markAll ? (
                <>
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  Marking...
                </>
              ) : unreadCount > 0 ? (
                <>
                  <CheckCheck className="w-5 h-5" />
                  Mark All Read ({unreadCount})
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  All Read
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-6 mb-8"
        >
          <div className="flex flex-col xl:flex-row gap-6">
            {/* Filter Select */}
            <div className="flex-shrink-0">
              <div className="relative">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="appearance-none w-full xl:w-auto px-6 py-4 rounded-2xl border border-gray-200 bg-white/80 backdrop-blur-sm shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-medium"
                >
                  <option value="all">All Notifications ({notifications.length})</option>
                  <option value="unread">Unread ({notifications.filter((n) => !n.is_read && !n.is_hidden).length})</option>
                  <option value="read">Read ({notifications.filter((n) => n.is_read && !n.is_hidden).length})</option>
                  {availableTypes
                    .filter((type) => !['info', 'success'].includes(type.toLowerCase()))
                    .map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)} (
                        {notifications.filter((n) => 
                          !n.is_hidden && (
                            n.type?.toLowerCase() === type.toLowerCase() || 
                            n.title?.toLowerCase() === type.toLowerCase()
                          )
                        ).length})
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-4.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/80 backdrop-blur-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-4.5 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Quick Stats */}
            <div className="flex items-center gap-6 px-6 py-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-100">
              <div className="flex items-center gap-2">
                <Inbox className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Total: {notifications.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-medium text-gray-700">Unread: {unreadCount}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Notifications List */}
        <div className="max-w-4xl mx-auto space-y-3">
          <AnimatePresence>
            {filteredNotifications.length === 0 ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-16 text-center"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-100 to-purple-100 rounded-3xl blur-2xl opacity-30"></div>
                  <div className="relative bg-gradient-to-r from-blue-50 to-purple-50 p-8 rounded-3xl border border-blue-100">
                    <Inbox className="w-20 h-20 text-gray-400 mx-auto mb-6" />
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
                      {searchQuery ? "No matching notifications" : "All caught up!"}
                    </h3>
                    <p className="text-gray-600 text-lg">
                      {searchQuery
                        ? "Try adjusting your search terms or filters"
                        : "You're all caught up! New notifications will appear here."}
                    </p>
                  </div>
                </div>
              </motion.div>
            ) : (
              filteredNotifications.map((notif, index) => (
                <motion.div
                  key={notif.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ 
                    opacity: 1, 
                    x: 0
                  }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ 
                    duration: 0.2, 
                    delay: index * 0.03
                  }}
                  whileHover={{ x: 4 }}
                  className={`group relative bg-white/90 backdrop-blur-md rounded-lg shadow-sm border border-white/30 transition-all duration-200 hover:shadow-md hover:border-white/50 ${
                    !notif.is_read 
                      ? "bg-gradient-to-r from-blue-50/30 to-transparent border-l-4 border-l-blue-500" 
                      : "hover:bg-white"
                  }`}
                >
                  <div className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {/* Compact Icon */}
                      <div className={`relative flex-shrink-0`}>
                        <div className={`w-8 h-8 rounded-md bg-gradient-to-r ${getNotificationGradient(notif.type)} flex items-center justify-center text-white shadow-sm`}>
                          {getNotificationIcon(notif.type)}
                        </div>
                        {!notif.is_read && (
                          <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-white"></div>
                        )}
                      </div>

                      {/* Single Line Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <h3 className={`font-medium text-sm truncate ${!notif.is_read ? "text-gray-900" : "text-gray-700"}`}>
                              {notif.title}
                            </h3>
                            {notif.type && (
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getNotificationColor(notif.type)} flex-shrink-0`}>
                                {notif.type.toUpperCase()}
                              </span>
                            )}
                            <div
                              className={`text-xs text-gray-500 truncate max-w-xs ${!notif.is_read ? "font-medium" : ""}`}
                              dangerouslySetInnerHTML={{ __html: notif.message }}
                            />
                          </div>

                          {/* Time and Actions */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs text-gray-400 whitespace-nowrap">
                              {dayjs(notif.created_at).fromNow()}
                            </span>
                            
                            {/* Expand/Collapse Button */}
                            {notif.message && notif.message.length > 50 && (
                              <button
                                onClick={() => setSelectedNotification(selectedNotification === notif.id ? null : notif.id)}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                              >
                                {selectedNotification === notif.id ? 'Show less' : 'More'}
                              </button>
                            )}

                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => toggleReadStatus(notif.id, notif.is_read)}
                              disabled={actionLoading[`read_${notif.id}`]}
                              className={`p-1.5 rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                                notif.is_read 
                                  ? "text-gray-400 hover:text-blue-600 hover:bg-blue-50" 
                                  : "text-blue-600 bg-blue-50 hover:bg-blue-100"
                              }`}
                              title={notif.is_read ? "Mark as unread" : "Mark as read"}
                            >
                              {actionLoading[`read_${notif.id}`] ? (
                                <div className="w-3.5 h-3.5 border border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                              ) : notif.is_read ? (
                                <Eye className="w-3.5 h-3.5" />
                              ) : (
                                <EyeOff className="w-3.5 h-3.5" />
                              )}
                            </motion.button>

                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => toggleHideStatus(notif.id, notif.is_hidden)}
                              disabled={actionLoading[`hide_${notif.id}`]}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={notif.is_hidden ? "Unhide notification" : "Hide notification"}
                            >
                              {actionLoading[`hide_${notif.id}`] ? (
                                <div className="w-3.5 h-3.5 border border-red-600 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <X className="w-3.5 h-3.5" />
                              )}
                            </motion.button>
                          </div>
                        </div>

                        {/* Expanded Content */}
                        <AnimatePresence>
                          {selectedNotification === notif.id && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="pt-2 mt-2 border-t border-gray-100">
                                <div className="text-sm text-gray-600 leading-relaxed">
                                  <div dangerouslySetInnerHTML={{ __html: notif.message }} />
                                </div>
                                {notif.priority && (
                                  <div className="flex items-center gap-1 mt-2">
                                    <Star className="w-3 h-3 text-yellow-500" />
                                    <span className="text-xs font-medium text-yellow-600">
                                      {notif.priority}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>

        {/* Refresh Button */}
        {filteredNotifications.length > 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-12 text-center"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={fetchNotifications}
              className="inline-flex items-center gap-3 px-8 py-4 bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <RefreshCw className="w-5 h-5" />
              Refresh Notifications
            </motion.button>
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default NotificationsPage
